import { css, FlattenSimpleInterpolation } from 'styled-components';

import { CommonTheme, ManualCSSTheme } from 'app/shared/theme';
import { generateCSSPropForScreenSize } from 'app/shared/components/styleUtils/generateCSSPropForScreenSize';

type GapProp = string | number;

export interface WithGridProps {
  numberOfColumns?: number | number[];
  gap?: GapProp | GapProp[];
  rowGap?: GapProp | GapProp[];
  columnGap?: GapProp | GapProp[];
  alignContent?: string;
  alignItems?: string;
  justifyContent?: string;
  justifyItems?: string;
  gridAutoFlow?: string;
}

interface WithGridCSSProps extends WithGridProps {
  theme: CommonTheme | ManualCSSTheme;
}

function generateCssProperty(
  theme: CommonTheme | ManualCSSTheme,
  propName: string,
  value: string | number
) {
  if (propName === 'numberOfColumns') {
    return `grid-template-columns: repeat(${value}, 1fr);`;
  }

  const propMapping: { [key: string]: string } = {
    gap: 'grid-gap',
    rowGap: 'grid-row-gap',
    columnGap: 'grid-column-gap',
    alignContent: 'align-content',
    alignItems: 'align-items',
    justifyContent: 'justify-content',
    justifyItems: 'justify-items',
    gridAutoFlow: 'grid-auto-flow',
  };

  if (typeof value === 'number') {
    return `${propMapping[propName]}: ${theme.ruler[value]}px;`;
  }

  return `${propMapping[propName]}: ${value};`;
}

const generateCSSForScreenSize = (
  screenSize: string,
  { theme, ...props }: WithGridCSSProps
) => {
  let s = '';

  Object.keys(props).forEach((propName) => {
    s += generateCSSPropForScreenSize({
      theme,
      screenSize,
      propName,
      value: props[propName as keyof WithGridProps],
      generateCssProperty,
    });
  });

  return css`${s}`;
};

export const withGridCSS = (props: WithGridCSSProps) => {
  const { theme } = props;
  let styles: (string | FlattenSimpleInterpolation)[] = [
    'width: 100%;',
    'display: grid;',
  ];

  Object.keys(theme.media).forEach((breakPointName) => {
    const generatedCSS = generateCSSForScreenSize(breakPointName, props);

    if (generatedCSS.length > 0) {
      const mediaQuery = theme.utils.getMediaQuery(breakPointName);
      styles.push(mediaQuery`${generatedCSS}`);
    }
  });

  return styles;
};
